import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Stethoscope, Calendar, Users, Star, ArrowRight, ArrowUpRight,
  MapPin, Briefcase, Sparkles, CheckCircle2, Plane, Microscope, Dumbbell,
  Crown, Video, Network, Award, Headphones, BadgeCheck, HeartHandshake,
  ExternalLink, Quote, Clock, Building2, UserCheck, Scale, Globe,
} from "lucide-react";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import SMSRecruiterCard, { SMS_COPY } from "../../components/SMSRecruiterCard";
import PageMeta from "../../components/seo/PageMeta";
import { SHIFT_CONNECT_LOGO } from "../../components/TritalLogo";
import { api } from "../../lib/api";

const SHIFTBRIDGE_LINK = "https://shifts.tritalcare.com/";

/* ------------------------------------------------------------------ */
/*  Editorial imagery                                                  */
/* ------------------------------------------------------------------ */
const HERO_IMG          = "https://images.pexels.com/photos/6129494/pexels-photo-6129494.jpeg";
const EDITORIAL_NURSE   = "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg";
const EDITORIAL_LEADER  = "https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg";
const EDITORIAL_TEAM    = "https://images.pexels.com/photos/7089401/pexels-photo-7089401.jpeg";

/* ------------------------------------------------------------------ */
/*  Static content                                                     */
/* ------------------------------------------------------------------ */

const TRUST_THEMES = [
  { Icon: ShieldCheck,    t: "Compliance-Driven Delivery",   d: "Joint Commission-aligned credentialing on every placement." },
  { Icon: Network,        t: "MSP / VMS Ready",              d: "Plug into managed-service programs on day one." },
  { Icon: Award,          t: "Supplier Diversity Certified", d: "NCTRCA MBE/SBE credentials with documented supplier-diversity review." },
  { Icon: Headphones,     t: "Responsive Workforce Support", d: "24/7 clinical support — typical reply in 4 business hours." },
  { Icon: BadgeCheck,     t: "Quality-Focused Placement",    d: "Competency-verified, reference-checked, fit-matched." },
];

const TRUST_LOGOS = ["Travel Nursing", "Local & PRN", "Allied Health", "Therapy", "Physicians & APPs", "Direct Hire"];

const SERVICES = [
  { Icon: Plane,        t: "Travel Nursing",            d: "13-week+ RN contracts nationwide.",                 to: "/services/travel-nursing" },
  { Icon: MapPin,       t: "Local & Per Diem",          d: "Same-week local and PRN coverage.",                 to: "/services/local-per-diem-staffing" },
  { Icon: Microscope,   t: "Allied Health",             d: "Lab, imaging, respiratory professionals.",          to: "/services/allied-health-staffing" },
  { Icon: Dumbbell,     t: "Therapy",                   d: "PT, OT, SLP placements across settings.",           to: "/services/therapy-staffing" },
  { Icon: Stethoscope,  t: "Physician & APP",           d: "MD, DO, NP, PA locum and permanent.",               to: "/services/physician-app-staffing" },
  { Icon: Crown,        t: "Executive Leadership",      d: "Interim CNO, CMO and directors.",                   to: "/services/executive-leadership" },
  { Icon: Video,        t: "Telehealth",                d: "Virtual-care, multi-state licensed clinicians.",    to: "/services/telehealth-staffing" },
  { Icon: Briefcase,    t: "Contract & Direct Hire",    d: "Temp-to-perm and permanent clinical hiring.",       to: "/services/contract-direct-hire" },
];

const WHY = [
  { Icon: ShieldCheck,   t: "Credentialing before submission",  d: "Every applicant is primary-source verified before a facility sees their profile." },
  { Icon: BadgeCheck,    t: "Competency, not just résumé",       d: "Specialty-specific skill checklists scored on years + self-rating + recruiter sign-off." },
  { Icon: Network,       t: "VMS-ready out of the box",          d: "Submission, timekeeping and billing integrations supported from the first placement." },
  { Icon: Users,         t: "Clinician-first economics",         d: "Transparent pay packages — taxable base, stipends, OT and bonuses shown before signing." },
  { Icon: Globe,         t: "National reach, local depth",       d: "Coverage across all 50 states, anchored by our Texas operations and India support centers." },
  { Icon: HeartHandshake, t: "Support that actually answers",    d: "24/7 operations team — typical response within 4 business hours, measured weekly." },
];

const COMPLIANCE_POINTS = [
  "Joint Commission-aligned credentialing program",
  "ISO 27001-aligned controls on credential and PII data",
  "HIPAA-aware workflows with field-level audit logs",
  "SOC 2 aligned data handling",
  "Primary-source license verification on every placement",
  "Specialty-competency verification via research-backed checklists",
];

const DIVERSITY = [
  { Icon: Scale,          t: "Equitable hiring",        d: "Structured interviews, blinded reviews, bias-interruption training across every recruiter team." },
  { Icon: HeartHandshake, t: "Supplier diversity",      d: "Active partnerships with MBE / WBE / VOSB / DOBE clinical staffing firms — transparent reporting for procurement." },
  { Icon: Users,          t: "Clinician ERGs",          d: "Employee Resource Groups for nurses of color, veterans, LGBTQ+ clinicians and first-gen clinicians." },
  { Icon: BadgeCheck,     t: "Cultural-competency training", d: "Required for every recruiter and placed clinician — language access, disability etiquette, health equity." },
];

const CERTIFICATIONS = [
  "Joint Commission-Aligned",
  "HIPAA-Aware",
  "SOC 2 Aligned",
  "ISO 27001 Controls",
  "MBE / WBE Program Partner",
  "VOSB Friendly",
];

const EMPLOYER_STEPS = [
  { n: "01", t: "Share requirements", d: "Specialty, unit,  type, pay band, compliance rules — submitted once per requisition." },
  { n: "02", t: "Curated matches",     d: "Credentialed, competency-verified clinicians matched within 48 hours across our network." },
  { n: "03", t: "Submit & onboard",    d: "Facility-specific checklist, licensure and orientation handled end-to-end." },
  { n: "04", t: "Operate & renew",     d: "Timekeeping, billing and performance reviewed on a single operational pane." },
];

const CLINICIAN_STEPS = [
  { n: "01", t: "Create profile",        d: "One profile. Credentials, specialties and skill checklists travel with you." },
  { n: "02", t: "Match to assignments",  d: "See only roles you qualify for — travel, local, per-diem or permanent." },
  { n: "03", t: "Apply with confidence", d: "Transparent pay, housing support and onboarding plan before you commit." },
  { n: "04", t: "Start. Renew. Grow.",   d: "Assignment management, support, and the next role queued up when you're ready." },
];

const INSIGHTS = [
  {
    cat: "Workforce Strategy",
    title: "MSP vs VMS: what health systems actually need in 2026",
    read: "9 min read",
    to: "/blog",
  },
  {
    cat: "Clinician Career",
    title: "Travel nurse pay packages, explained line by line",
    read: "7 min read",
    to: "/blog",
  },
  {
    cat: "Compliance & Regulatory",
    title: "Joint Commission audit readiness for staffing partners",
    read: "12 min read",
    to: "/blog",
  },
];

/* ------------------------------------------------------------------ */
/*  Small primitives                                                   */
/* ------------------------------------------------------------------ */

const Eyebrow = ({ children, tone = "red" }) => (
  <div className={`eyebrow ${tone === "red" ? "text-trital-red" : "text-slate-500"}`}>{children}</div>
);

const H2 = ({ children, className = "" }) => (
  <h2 className={`font-display font-black text-3xl lg:text-[44px] leading-[1.08] text-slate-900 text-balance ${className}`}>
    {children}
  </h2>
);

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.45, ease: [0.16, 0.84, 0.44, 1] },
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function Home() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then((r) => {
      if (r?.data?.source === "current_database" && r.data.verified === true) setStats(r.data);
    }).catch(() => setStats(null));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <PageMeta
        title="Healthcare Workforce, On One System | TRITAL Care®"
        description="Travel, PRN, local and permanent clinical staffing — orchestrated on a compliance-first workforce platform for hospitals, health systems, MSPs and the clinicians who power them."
        canonical="https://www.tritalcare.com/"
        ogImage={HERO_IMG}
      />
      <PublicNav />

      {/* Animated featured-opportunities ribbon, immediately below the brand navigation. */}
      <Link
        to="/hot-jobs"
        aria-label="Explore TRITAL Care Hot Jobs: Travel Nursing, Allied Health and Therapy"
        data-testid="homepage-hot-jobs-banner"
        className="hotjobs-ribbon group relative isolate block overflow-hidden text-white border-y border-[#263b77]"
      >
        <span className="hotjobs-ribbon-glow pointer-events-none absolute inset-0" aria-hidden="true" />
        <span className="hotjobs-ribbon-shine pointer-events-none absolute inset-0" aria-hidden="true" />
        <span className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 min-h-[47px] py-2 flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2 sm:gap-3">
            <span className="relative inline-flex w-3 h-3 shrink-0 items-center justify-center" aria-hidden="true">
              <span className="absolute w-3 h-3 rounded-full bg-[#ff6477] animate-ping motion-reduce:animate-none opacity-60" />
              <span className="relative w-2 h-2 rounded-full bg-[#ff5268] shadow-[0_0_14px_4px_rgba(255,80,105,0.6)]" />
            </span>
            <span className="shrink-0 rounded-full bg-[#e62c48] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] shadow-[0_0_18px_rgba(232,49,77,0.42)]">Hot Jobs</span>
            <span className="min-w-0 truncate text-[11px] sm:text-[13px] font-semibold tracking-tight">
              Travel Nursing <span className="text-white/50">·</span> Allied Health <span className="text-white/50">·</span> Therapy <span className="hidden md:inline text-white/60">— opportunities nationwide</span>
            </span>
          </span>
          <span className="shrink-0 inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-bold text-white transition-colors group-hover:text-[#ffc9d4]">
            <span className="hidden sm:inline">Explore opportunities</span>
            <span className="sm:hidden">Explore</span>
            <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform motion-reduce:transform-none" strokeWidth={2} />
          </span>
        </span>
      </Link>

      {/* ============================== 2 · HERO ============================== */}
      <section className="relative overflow-hidden bg-white" data-testid="home-hero">
        <div className="absolute inset-0 bg-trital-mesh" aria-hidden="true" />
        <div className="absolute inset-0 dot-grid opacity-60" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-12 lg:pb-16 grid lg:grid-cols-12 gap-8 items-center">
          {/* Copy */}
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
                <span className="relative flex items-center justify-center w-2 h-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-trital-red opacity-70 animate-pulse-red" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-trital-red" />
                </span>
                <span className="overline text-trital-navy">Healthcare staffing, designed around people.</span>
              </div>

              <h1 className="mt-6 font-display font-black text-[44px] leading-[1.02] sm:text-5xl lg:text-[72px] lg:leading-[1.02] tracking-tight text-slate-900 text-balance">
                Exceptional care starts with{" "}
                <span className="text-trital-navy">the right people.</span>{" "}
                <span className="text-trital-red">At the right time.</span>
              </h1>

              <p className="mt-6 max-w-xl text-[16px] lg:text-[17px] text-slate-600 leading-relaxed">
                Travel, local, PRN and permanent healthcare staffing. Connecting clinicians, hospitals and health systems through a more thoughtful, responsive workforce experience.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/register?role=facility"
                  className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep shadow-[0_10px_30px_-10px_rgba(19,39,122,0.45)]"
                  data-testid="hero-cta-request-talent"
                >
                  Request Talent <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
                </Link>
                <Link
                  to="/register?role=clinician"
                  className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-red text-white font-semibold btn-lift hover:bg-trital-red-deep"
                  data-testid="hero-cta-find-opportunities"
                >
                  Find Opportunities <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
                </Link>
                <a
                href={SHIFTBRIDGE_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-slate-300 bg-white text-trital-navy font-semibold hover:border-trital-navy hover:bg-slate-50 btn-lift"
                aria-label="Explore TRITAL ShiftBridge PRN shifts"
                data-testid="hero-cta-prn-shifts"
              >
                PRN Shifts <ExternalLink className="w-4 h-4 opacity-60" strokeWidth={1.8} />
              </a>

    
              </div>

              {/* Subtle trust statement */}
              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-slate-500">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> Clinical-first recruiting</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> Documented compliance workflows</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> Dedicated workforce support</span>
              </div>

              {/* Database-backed operational indicators. Never invent counts when the API is offline. */}
              <div className="mt-9 max-w-xl" data-testid="hero-stats">
                {stats ? (
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      {v:stats.clinicians,l:"Active clinician profiles"},
                      {v:stats.shifts,l:"Open PRN shifts"},
                      {v:stats.jobs,l:"Open job listings"}
                    ].map(item=><div key={item.l} className="border border-slate-200 bg-white rounded-xl px-3 py-4 shadow-sm">
                      <div className="font-display font-black text-2xl sm:text-3xl text-trital-navy tabular-nums">
                        {Number.isFinite(item.v) ? item.v.toLocaleString("en-US") : "—"}
                      </div>
                      <div className="text-[11px] mt-1 font-semibold text-slate-500">{item.l}</div>
                    </div>)}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-x-5 gap-y-3 text-sm text-slate-500">
                    <span className="inline-flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-trital-red"/> Travel & Local</span>
                    <span className="inline-flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-trital-red"/> PRN Workforce</span>
                    <span className="inline-flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-trital-red"/> Permanent Placement</span>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Hero visual */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative rounded-3xl overflow-hidden shadow-navy"
            >
              <img src={HERO_IMG} alt="Clinical team in a hospital corridor" className="w-full h-[480px] lg:h-[560px] object-cover" style={{ filter: "saturate(0.92) contrast(1.02)" }} draggable={false} />
              {/* Bottom gradient */}
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-trital-navy-ink/70 via-trital-navy-ink/20 to-transparent" aria-hidden="true" />

              {/* Service gateway — no invented requisition or pay rate. */}
              <div className="absolute top-5 left-5 right-5 sm:right-auto sm:w-[280px] rounded-2xl p-4 bg-white/95 border border-white/80 shadow-xl">
                <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-trital-red">Explore opportunities</div>
                <div className="mt-2 font-display font-black text-[17px] text-[#0e2c65]">Your next healthcare opportunity starts here.</div>
                <Link to="/careers/jobs" className="mt-3 text-sm font-bold text-trital-navy hover:text-trital-red inline-flex gap-1 items-center">Search current jobs <ArrowRight className="w-4 h-4"/></Link>
              </div>

              {/* Floating trust chip */}
              <div className="absolute bottom-5 left-5 right-5 sm:right-auto sm:max-w-[340px] glass-card rounded-2xl p-4 bg-white/94 border-white/70">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-trital-navy text-white flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="font-display font-bold text-[14px] text-slate-900">Credentialing-led staffing</div>
                    <div className="text-[12px] text-slate-600">A focus on documentation, quality and readiness.</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SMS recruiter access — directly under hero */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pt-10" data-testid="home-sms-top">
        <SMSRecruiterCard variant="default" headline={SMS_COPY.hero.headline} subtext={SMS_COPY.hero.sub} />
      </section>

      {/* ============================== 3 · TRUST BAR ============================== */}
      <section className="border-y border-slate-200 bg-white" data-testid="home-trust-bar">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-8 gap-x-4">
            {TRUST_THEMES.map((t) => (
              <div key={t.t} className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center shrink-0">
                  <t.Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="font-display font-bold text-[14px] text-slate-900 leading-tight">{t.t}</div>
                  <div className="mt-0.5 text-[12.5px] text-slate-500 leading-snug">{t.d}</div>
                </div>
              </div>
            ))}
          </div>
          {/* Logos strip */}
          <div className="mt-10 pt-8 border-t border-slate-100 flex items-center gap-4 flex-wrap">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400 shrink-0">Workforce specialties</div>
            <div className="flex items-center gap-x-8 gap-y-2 flex-wrap text-slate-500 font-display font-bold text-sm">
              {TRUST_LOGOS.map((l) => <span key={l} className="opacity-80 hover:opacity-100 transition-opacity">{l}</span>)}
            </div>
          </div>
        </div>
      </section>

      {/* ============================== 4 · AUDIENCE SPLIT ============================== */}
      <section className="section-pad bg-white" data-testid="home-audience-split">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <motion.div {...fadeUp} className="max-w-[760px] mb-12">
            <Eyebrow>Built for both sides of the marketplace</Eyebrow>
            <H2 className="mt-3">One platform. Two audiences. Zero friction.</H2>
            <p className="mt-5 text-[17px] text-slate-600 max-w-[56ch]">
              Every feature is designed against two jobs-to-be-done: a facility needs compliant coverage, a clinician
              needs transparent work. We optimize for both without compromise.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Employers */}
            <motion.div {...fadeUp} className="relative overflow-hidden rounded-3xl bg-trital-navy text-white p-8 lg:p-10">
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/8 blur-3xl" aria-hidden="true" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-trital-red">
                  <Building2 className="w-4 h-4" strokeWidth={1.8} /> For Employers
                </div>
                <h3 className="mt-4 font-display font-black text-2xl lg:text-[32px] leading-[1.1] text-balance">
                  Your entire workforce, on one system of record.
                </h3>
                <p className="mt-3 text-white/75 max-w-[44ch]">
                  Travel, PRN, local, contract and permanent placements — orchestrated against your facility compliance rules.
                </p>
                <ul className="mt-6 space-y-2.5 text-[14px] text-white/85">
                  {[
                    "Credentialing verified before submission",
                    "MSP / VMS integration from day one",
                    "Transparent bill rates and billing",
                    "Same-week PRN fill standard",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-trital-red shrink-0" strokeWidth={1.8} /> {b}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register?role=facility"
                  className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-slate-900 text-sm font-semibold btn-lift hover:bg-slate-100"
                  data-testid="audience-cta-employer"
                >
                  Request Talent <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
              </div>
            </motion.div>

            {/* Clinicians */}
            <motion.div {...fadeUp} className="relative overflow-hidden rounded-3xl bg-trital-red text-white p-8 lg:p-10">
              <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-white/80">
                  <UserCheck className="w-4 h-4" strokeWidth={1.8} /> For Clinicians
                </div>
                <h3 className="mt-4 font-display font-black text-2xl lg:text-[32px] leading-[1.1] text-balance">
                  Verified assignments. Transparent pay. Portable credentials.
                </h3>
                <p className="mt-3 text-white/85 max-w-[44ch]">
                  Browse travel, per-diem and permanent roles at credentialed facilities — your profile travels with you.
                </p>
                <ul className="mt-6 space-y-2.5 text-[14px] text-white/90">
                  {[
                    "Pay transparency before you sign",
                    "Credentials travel facility-to-facility",
                    "Compact-license fast-tracking",
                    "Support that actually answers",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-white shrink-0" strokeWidth={1.8} /> {b}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register?role=clinician"
                  className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-slate-900 text-sm font-semibold btn-lift hover:bg-slate-100"
                  data-testid="audience-cta-clinician"
                >
                  Find Opportunities <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================== 5 · SERVICE CARDS GRID ============================== */}
      <section className="section-pad-sm bg-trital-mist" data-testid="home-services">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <motion.div {...fadeUp} className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div className="max-w-[720px]">
              <Eyebrow>Capabilities</Eyebrow>
              <H2 className="mt-3">Every clinical discipline. One workforce system.</H2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-1.5 text-sm font-semibold text-trital-navy hover:gap-2 transition-all" data-testid="services-learn-more">
              All services <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.04, ease: [0.16, 0.84, 0.44, 1] }}
              >
                <Link
                  to={s.to}
                  className="group block card-premium card-premium-hover rounded-2xl p-6 h-full bg-white"
                  data-testid={`service-card-${s.to.split("/").pop()}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center group-hover:bg-trital-navy group-hover:text-white transition-colors">
                    <s.Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
                  </div>
                  <div className="mt-5 font-display font-bold text-[17px] text-slate-900 group-hover:text-trital-navy flex items-center gap-1">
                    {s.t}
                    <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-trital-red" strokeWidth={1.8} />
                  </div>
                  <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{s.d}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== 5b · PRN MARKETPLACE TEASER (TRITAL ShiftBridge™) ============================== */}
      <section className="relative overflow-hidden bg-trital-navy-mesh text-white" data-testid="home-prn-teaser">
        <div className="absolute inset-0 dot-grid opacity-10" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-[1.25fr_1fr] gap-12 items-center">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.22em] font-bold text-trital-red">
              <span className="relative flex items-center justify-center w-2 h-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-trital-red opacity-70 animate-pulse-red" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-trital-red" />
              </span>
              Platform brand · Launching soon
            </div>
            <h2 className="mt-6 font-display font-black text-4xl lg:text-[56px] leading-[1.02] tracking-tight text-balance">
              PRN shifts. <span className="text-trital-red">Picked up</span> in minutes.
            </h2>
            <p className="mt-5 text-white/80 text-[17px] leading-relaxed max-w-[58ch]">
              <span className="font-semibold text-white">TRITAL ShiftBridge™</span> is our PRN and per-diem shift marketplace —
              where credentialed clinicians pick up local shifts in minutes and facilities fill open coverage in under
              72 hours. Launching on{" "}
              <a
                href={SHIFTBRIDGE_LINK} target="_blank" rel="noopener noreferrer"
                className="font-semibold text-white underline-offset-4 hover:underline"
                data-testid="announcement-cta"
              >
                shifts.tritalcare.com
              </a>
              {" "}
              with iOS and Android apps to follow.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/register?role=facility"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white text-sm font-semibold btn-lift hover:bg-trital-navy-deep border border-white/10"
                data-testid="prn-cta-facility"
              >
                Post a Shift <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link
                to="/register?role=clinician"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white text-sm font-semibold btn-lift hover:bg-trital-red-deep"
                data-testid="prn-cta-clinician"
              >
                Pick Up Shifts <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <a
                href={SHIFTBRIDGE_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/25 text-white text-sm font-semibold hover:bg-white/10"
                aria-label="Open TRITAL ShiftBridge marketplace (opens in new tab)"
                data-testid="prn-cta-open-subdomain"
              >
                Open shifts.tritalcare.com <ExternalLink className="w-4 h-4" strokeWidth={1.8} />
              </a>
              
            </div>

            <div className="mt-6">
              <a href={SHIFTBRIDGE_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[13px] text-white/70 hover:text-white" data-testid="prn-cta-learn-more">
                Learn more about TRITAL ShiftBridge™ → <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              </a>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="lg:justify-self-end">
            <div className="glass-card rounded-3xl p-7 bg-white/94 border-white/70 max-w-sm">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-trital-red mb-4 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Platform brand
              </div>
              <img
                src={SHIFT_CONNECT_LOGO}
                alt="TRITAL Care® TRITAL ShiftBridge™ — Connecting Clinicians. Powering Care."
                className="max-h-20 w-auto object-contain mx-auto select-none"
                draggable={false}
              />
              <div className="mt-5 pt-4 border-t border-slate-200 text-[12px] text-slate-600 leading-relaxed">
                TRITAL ShiftBridge™ is a sub-brand of{" "}
                <Link to="/" className="font-semibold text-trital-navy hover:underline underline-offset-4">TRITAL Care®</Link>,
                the operating brand of TOPTAL Care Inc.
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================== 6 · WHY TRITAL CARE ============================== */}
      <section className="section-pad bg-white" data-testid="home-why">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 grid lg:grid-cols-[1fr_2fr] gap-12">
          <motion.div {...fadeUp}>
            <Eyebrow>Why TRITAL Care</Eyebrow>
            <H2 className="mt-3">The infrastructure behind a trusted clinical workforce.</H2>
            <p className="mt-5 text-slate-600 max-w-[48ch]">
              We treat staffing as an operational problem, not a recruiting one. The platform, the team and the
              processes all aim at one outcome — compliant, high-quality coverage your team can trust.
            </p>
            <div className="mt-8 rounded-2xl overflow-hidden shadow-card">
              <img src={EDITORIAL_NURSE} alt="Clinician at a workstation" className="w-full h-64 object-cover" style={{ filter: "saturate(0.92) contrast(1.02)" }} />
            </div>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4">
            {WHY.map((w, i) => (
              <motion.div
                key={w.t}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="card-premium card-premium-hover rounded-2xl p-6"
              >
                <div className="w-10 h-10 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
                  <w.Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
                </div>
                <div className="mt-4 font-display font-bold text-[16px] text-slate-900">{w.t}</div>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{w.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== 7 · COMPLIANCE & QUALITY ============================== */}
      <section className="section-pad-sm bg-trital-mist" data-testid="home-compliance">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 grid lg:grid-cols-[1fr_1fr] gap-12 items-center">
          <motion.div {...fadeUp}>
            <Eyebrow>Compliance & quality</Eyebrow>
            <H2 className="mt-3">Audit-ready, by design.</H2>
            <p className="mt-5 text-slate-600 max-w-[52ch]">
              Compliance isn't a checkbox at submission — it's engineered into every workflow. Credentialing,
              competency, licensure and audit trail travel with every clinician placement.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-y-3 gap-x-6">
              {COMPLIANCE_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-[14px] text-slate-800">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-trital-red shrink-0" strokeWidth={1.8} /> {p}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/compliance" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white text-sm font-semibold btn-lift hover:bg-trital-navy-deep" data-testid="compliance-cta-primary">
                Compliance overview <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link to="/compliance/checklists" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy text-sm font-semibold hover:border-trital-navy">
                Skill checklists
              </Link>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="relative">
            <div className="rounded-3xl overflow-hidden shadow-navy">
              <img src={EDITORIAL_LEADER} alt="Healthcare leadership reviewing documents" className="w-full h-[420px] object-cover" style={{ filter: "saturate(0.92) contrast(1.02)" }} />
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-trital-navy-ink/60 to-transparent" aria-hidden="true" />
            </div>
            {/* Floating cert chips */}
            <div className="absolute -bottom-4 left-4 right-4 flex flex-wrap gap-2 justify-center">
              {["Joint Commission-aligned", "SOC 2", "HIPAA-aware"].map((c) => (
                <span key={c} className="glass-card bg-white/95 rounded-full px-3.5 py-1.5 text-[11px] font-semibold text-slate-700">
                  {c}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================== 8 · SUPPLIER DIVERSITY & CERTIFICATIONS ============================== */}
      <section className="section-pad bg-white" data-testid="home-diversity">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <motion.div {...fadeUp} className="max-w-[720px] mb-10">
            <Eyebrow>Supplier diversity & certifications</Eyebrow>
            <H2 className="mt-3">Equity in how we hire, staff and certify.</H2>
            <p className="mt-5 text-slate-600 max-w-[56ch]">
              Healthcare serves the full breadth of humanity. The workforce that delivers that care should too — backed
              by measurable commitments and procurement-friendly documentation.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DIVERSITY.map((d, i) => (
              <motion.div
                key={d.t}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="card-premium card-premium-hover rounded-2xl p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
                  <d.Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
                </div>
                <div className="mt-4 font-display font-bold text-[16px] text-slate-900">{d.t}</div>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{d.d}</p>
              </motion.div>
            ))}
          </div>

          {/* Certification chips */}
          <div className="mt-10 border-t border-slate-100 pt-8 flex flex-wrap items-center gap-3">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400 shrink-0">Our posture</div>
            {CERTIFICATIONS.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-trital-mist border border-slate-200 text-[12px] font-semibold text-trital-navy">
                <BadgeCheck className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> {c}
              </span>
            ))}
            <Link to="/diversity-and-inclusion" className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-trital-navy hover:gap-2 transition-all" data-testid="diversity-learn-more">
              Read our commitment <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================== 9 · EMPLOYER PROCESS ============================== */}
      <section className="section-pad-sm bg-trital-mist" data-testid="home-employer-process">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <motion.div {...fadeUp} className="max-w-[720px] mb-10">
            <Eyebrow>Employer process</Eyebrow>
            <H2 className="mt-3">Scope to placement in four steps.</H2>
          </motion.div>

          <div className="relative grid md:grid-cols-4 gap-4">
            {/* connecting line */}
            <div className="hidden md:block absolute top-7 left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-trital-navy/20 to-transparent" aria-hidden="true" />
            {EMPLOYER_STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className="relative bg-white rounded-2xl border border-slate-200 p-6"
              >
                <div className="w-14 h-14 rounded-full bg-trital-navy text-white font-display font-black text-lg tabular-nums flex items-center justify-center">
                  {s.n}
                </div>
                <div className="mt-4 font-display font-bold text-[16px] text-slate-900">{s.t}</div>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8">
            <Link to="/register?role=facility" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white text-sm font-semibold btn-lift hover:bg-trital-navy-deep" data-testid="employer-process-cta">
              Request Talent <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================== 10 · CLINICIAN PROCESS ============================== */}
      <section className="section-pad-sm bg-white" data-testid="home-clinician-process">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <motion.div {...fadeUp} className="max-w-[720px] mb-10">
            <Eyebrow>Clinician process</Eyebrow>
            <H2 className="mt-3">Profile to placement in four steps.</H2>
          </motion.div>

          <div className="relative grid md:grid-cols-4 gap-4">
            <div className="hidden md:block absolute top-7 left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-trital-red/20 to-transparent" aria-hidden="true" />
            {CLINICIAN_STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className="relative bg-white rounded-2xl border border-slate-200 p-6"
              >
                <div className="w-14 h-14 rounded-full bg-trital-red text-white font-display font-black text-lg tabular-nums flex items-center justify-center">
                  {s.n}
                </div>
                <div className="mt-4 font-display font-bold text-[16px] text-slate-900">{s.t}</div>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register?role=clinician" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white text-sm font-semibold btn-lift hover:bg-trital-red-deep" data-testid="clinician-process-cta">
              Find Opportunities <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link to="/compliance/checklists" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy text-sm font-semibold hover:border-trital-navy">
              Start a skills checklist
            </Link>
          </div>
        </div>
      </section>

      {/* Replace unverified testimonial quotes with transparent service pillars. */}
      <section className="relative bg-trital-navy-ink text-white" data-testid="home-workforce-commitment">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-60 pointer-events-none"/>
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="max-w-[730px]">
            <div className="text-trital-red text-xs font-extrabold uppercase tracking-[0.16em]">Our workforce approach</div>
            <h2 className="font-display font-black text-3xl lg:text-[44px] leading-tight mt-4">The details behind a dependable placement.</h2>
            <p className="text-white/70 text-[15px] mt-4 leading-relaxed">Real recruiting decisions need responsive people, clear terms and thorough documentation. That's the operating standard we're building across TRITAL Care.</p>
          </div>
          <div className="mt-9 grid md:grid-cols-3 gap-4">
            {[
              {t:"Clinical-first sourcing",d:"Specialty-specific conversations built around the clinician and the facility need."},
              {t:"Credentialing workflow",d:"Track required documents, license review and assignment readiness before a start."},
              {t:"Clear pay conversations",d:"Use RateLab to prepare consistent, reviewable pay-package estimates."}
            ].map(item=><div key={item.t} className="rounded-2xl border border-white/15 bg-white/[0.06] p-6">
              <CheckCircle2 className="w-6 h-6 text-[#93b5f9]"/>
              <h3 className="font-display font-black text-xl mt-4">{item.t}</h3>
              <p className="mt-2 text-white/70 text-sm leading-relaxed">{item.d}</p>
            </div>)}
          </div>
        </div>
      </section>

      {/* ============================== 12 · FEATURED RESOURCES ============================== */}
      <section className="section-pad-sm bg-white" data-testid="home-insights">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <motion.div {...fadeUp} className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div className="max-w-[720px]">
              <Eyebrow>Resources</Eyebrow>
              <H2 className="mt-3">Insights from the field.</H2>
            </div>
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-trital-navy hover:gap-2 transition-all" data-testid="insights-view-all">
              View all resources <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {INSIGHTS.map((p, i) => (
              <motion.article
                key={p.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
              >
                <Link
                  to={p.to}
                  className="group block card-premium card-premium-hover rounded-2xl p-6 h-full"
                  data-testid={`insight-card-${p.title.toLowerCase().slice(0, 24).replace(/[^a-z]+/g, "-")}`}
                >
                  <div className="eyebrow text-trital-red">{p.cat}</div>
                  <h3 className="mt-3 font-display font-bold text-[18px] text-slate-900 leading-snug group-hover:text-trital-navy">
                    {p.title}
                  </h3>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-slate-500 inline-flex items-center gap-1.5">
                      <Clock className="w-3 h-3" strokeWidth={1.8} /> {p.read}
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-trital-red" strokeWidth={1.8} />
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== 13 · FINAL CTA BAND ============================== */}
      <section className="relative overflow-hidden bg-trital-navy-mesh text-white" data-testid="home-cta-band">
        <div className="absolute inset-0 dot-grid opacity-10" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-[1.3fr_1fr] items-center gap-10">
          <motion.div {...fadeUp}>
            <Eyebrow tone="red">Ready when you are</Eyebrow>
            <h3 className="mt-3 font-display font-black text-4xl lg:text-[56px] leading-[1.04] text-balance">
              Bring your next placement onto the <span className="text-trital-red">trusted workforce</span>.
            </h3>
            <p className="mt-5 text-white/80 max-w-[58ch]">
              Whether you're filling a unit this week or planning your next travel assignment, we'll meet you where you
              are — typical response within 4 business hours.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[320px]">
            <Link to="/register?role=facility" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-slate-900 text-sm font-semibold btn-lift hover:bg-slate-100" data-testid="final-cta-facility">
              Request Talent <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link to="/register?role=clinician" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-trital-red text-white text-sm font-semibold btn-lift hover:bg-trital-red-deep" data-testid="final-cta-clinician">
              Find Opportunities <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <a
                href={SHIFTBRIDGE_LINK} target="_blank" rel="noopener noreferrer"
                className="nline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-white/25 text-white text-sm font-semibold hover:bg-white/10" aria-label="PRN Shifts (opens in new tab)" data-testid="final-cta-prn"
              
              >
                PRN Shifts <ExternalLink className="w-4 h-4" strokeWidth={1.8} />
              </a>
           
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-white/70 text-sm font-semibold hover:text-white" data-testid="final-cta-contact">
              Or talk to our team →
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SMS recruiter access — text instead of call */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="home-sms-bottom">
        <SMSRecruiterCard variant="default" headline={SMS_COPY.default.headline} subtext={SMS_COPY.default.sub} />
      </section>

      <PublicFooter />
    </div>
  );
}

